import { Stack } from '@chakra-ui/react';
import React from 'react';

import { DCFSABenefits } from 'Containers/ViewBenefitPage/types';
import { transformBenefitItems } from 'Containers/ViewBenefitPage/utils';
import { useTextContext } from 'Contexts/textContext';
import { H5, Numbers, Paragraph } from 'DesignLibrary/atoms';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { SupplementalPlan } from 'Types/entities';
import { formatDate, toDollars } from 'Utils/helpers';
import { useFederalLimit } from 'Utils/useFederalLimit';

import { InfoBox, LimitRow } from './fsaPlanContent.styled';
import { DetailsWrapper } from './PlanContent/styled';

export interface DcfsaPlanContentProps {
  plan: SupplementalPlan;
}

export const DcfsaPlanContent = ({ plan }: DcfsaPlanContentProps) => {
  const { locale, retrieveContentfulData } = useTextContext();

  const benefitItems = transformBenefitItems(plan) as DCFSABenefits;

  const jvpFederalLimit = useFederalLimit('dependent_care_fsa');
  const federalLimit =
    jvpFederalLimit ?? retrieveContentfulData<number>('benefits_section_fsa.federal_limit');
  const contributionLimit = benefitItems.uses_federal_limit ? federalLimit : benefitItems.max_contribution;

  const incurDate = benefitItems.incur_date;
  const submitDate = benefitItems.submit_date;
  const hasDeadlines = !!incurDate && !!submitDate;

  return (
    <DetailsWrapper>
      <Stack gap={6}>
        {/* 1. Contribution limit -------------------------------------------- */}
        <LimitRow data-testid="fsa_dependent-contribution-limit">
          <H5>
            <Text field="benefits_section_fsa.contribution_limit_label" />
          </H5>
          <Numbers size="small" weight="normal">
            {toDollars(contributionLimit)}
          </Numbers>
        </LimitRow>

        {/* 2. Eligibility --------------------------------------------------- */}
        <Stack gap={2}>
          <H5>
            <Text field="benefits_section_fsa.eligibility_title" />
          </H5>
          <Paragraph size="small" lineHeight={1.75}>
            <Text field="benefits_section_fsa.eligibility_intro" />
          </Paragraph>
          <InfoBox>
            <Paragraph as="div" size="small" weight="bold" className="qualify-title">
              <Text field="benefits_section_fsa.qualify_title" />
            </Paragraph>
            <RichText
              field="benefits_section_fsa.qualify_list"
              inlineParagraphs={false}
              useStylingWrapper
              richTextStyling={{ p: { size: 'small', lineHeight: 1.75 } }}
              maxHeaderSize={4}
            />
            <Paragraph as="div" size="small" weight="bold" className="qualify-note">
              <Text field="benefits_section_fsa.qualify_note" />
            </Paragraph>
            <Stack mt={2}>
              <Paragraph size="small" lineHeight={1.75}>
                <Text
                  field="benefits_section_fsa.eligibility_irs_note"
                  vars={{
                    irsLink: (
                      <a href="https://www.irs.gov/pub/irs-pdf/p503.pdf" target="_blank" rel="noreferrer">
                        IRS publication 503
                      </a>
                    ),
                  }}
                />
              </Paragraph>
            </Stack>
          </InfoBox>
        </Stack>

        {/* 3. Important deadlines ------------------------------------------- */}
        {hasDeadlines && (
          <Stack gap={2}>
            <H5>
              <Text field="benefits_section_fsa.important_deadlines_title" />
            </H5>
            <Paragraph size="small" lineHeight={1.75}>
              <Text
                field="benefits_section_fsa.deadlines"
                vars={{
                  incurDate: formatDate(incurDate, locale),
                  submitDate: formatDate(submitDate, locale),
                }}
              />
            </Paragraph>
          </Stack>
        )}
      </Stack>
    </DetailsWrapper>
  );
};
